// Timezone Context for global timezone management
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  formatDateWithTimezone, 
  formatTimeWithTimezone, 
  formatDateTimeWithTimezone,
  formatAppointmentDateTime,
  getCurrentDateInTimezone,
  getCurrentTimeInTimezone,
  isAppointmentToday,
  isAppointmentPast,
  getTimezoneName
} from '../utils/timezone';
import authService from '../services/auth';

const TimezoneContext = createContext();

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export const TimezoneProvider = ({ children }) => {
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [timezoneName, setTimezoneName] = useState('India Standard Time (IST)');

  // Load timezone from tenant data when component mounts
  useEffect(() => {
    const loadTimezone = () => {
      try {
        const tenant = authService.getTenant();
        if (tenant && tenant.timezone) {
          setTimezone(tenant.timezone);
          setTimezoneName(getTimezoneName(tenant.timezone));
        }
      } catch (error) {
        console.error('Error loading timezone:', error);
      }
    };

    loadTimezone();
  }, []);

  // Update timezone (useful for when tenant data changes)
  const updateTimezone = (newTimezone) => {
    setTimezone(newTimezone);
    setTimezoneName(getTimezoneName(newTimezone));
  };

  // Format functions with current timezone
  const formatDate = (date, options = {}) => {
    return formatDateWithTimezone(date, timezone, options);
  };

  const formatTime = (time, options = {}) => {
    return formatTimeWithTimezone(time, timezone, options);
  };

  const formatDateTime = (datetime, options = {}) => {
    return formatDateTimeWithTimezone(datetime, timezone, options);
  };

  const formatAppointment = (date, time) => {
    return formatAppointmentDateTime(date, time, timezone);
  };

  const getCurrentDate = () => {
    return getCurrentDateInTimezone(timezone);
  };

  const getCurrentTime = () => {
    return getCurrentTimeInTimezone(timezone);
  };

  const isToday = (appointmentDate) => {
    return isAppointmentToday(appointmentDate, timezone);
  };

  const isPast = (appointmentDate, appointmentTime) => {
    return isAppointmentPast(appointmentDate, appointmentTime, timezone);
  };

  const value = {
    timezone,
    timezoneName,
    updateTimezone,
    formatDate,
    formatTime,
    formatDateTime,
    formatAppointment,
    getCurrentDate,
    getCurrentTime,
    isToday,
    isPast,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
